import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Download, Plus, CheckCircle2, Clock, Search } from 'lucide-react';
import api from '../api/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from '../hooks/use-toast';

// Le registre que demandent les indicateurs 31 et 32 : ce qui a derape, et ce
// qu'on en a fait. Les regles — l'etat, le refus de clore sans mesure, le
// delai — vivent au serveur : deux calculs du meme etat finiraient par ne pas
// peindre la meme ligne en rouge.

const VIDE = {
  survenu_le: '', origine_code: 'APPRENANT', type_code: 'RECLAMATION',
  gravite_code: 'MINEURE', entite_libelle: '', description: '',
  analyse: '', mesure: '', responsable: '', echeance: '', resultat: '',
  reclamant: '', reponse_faite: '', reponse_le: '',
};

// Ce qui attend une reponse **a la personne**, en plus de la mesure interne.
// La meme liste qu'au serveur : une panne de video-projecteur se corrige, elle
// ne se repond pas. C'est le serveur qui compte et qui signale ; l'ecran s'en
// sert seulement pour ne pas montrer deux cases qui n'ont aucun sens sur un
// alea technique.
const TYPES_AVEC_REPONSE = ['RECLAMATION', 'INSATISFACTION'];

const COULEUR_STATUT = {
  ouvert: 'bg-[#EEF2FF] text-[#4F46E5]',
  en_cours: 'bg-[#ECFDF5] text-[#047857]',
  en_retard: 'bg-[#FEF2F2] text-[#B91C1C]',
  clos: 'bg-[#F6F7FB] text-[#6B6C85]',
};

const jourFr = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR');
};

const pourChamp = (v) => (v ? String(v).slice(0, 10) : '');

export default function RegistreEvenements() {
  const [evenements, setEvenements] = useState([]);
  const [bilan, setBilan] = useState(null);
  const [refs, setRefs] = useState({ origines: [], types: [], gravites: [] });
  const [chargement, setChargement] = useState(true);
  const [ouvert, setOuvert] = useState(null); // l'evenement dans la fenetre
  const [recherche, setRecherche] = useState('');
  const [vue, setVue] = useState('ouverts');

  const charger = () => api.get('/evenements')
    .then(({ data }) => { setEvenements(data.evenements || []); setBilan(data.bilan); })
    .catch(() => {})
    .finally(() => setChargement(false));

  useEffect(() => {
    charger();
    api.get('/evenements/referentiels').then(({ data }) => setRefs(data)).catch(() => {});
  }, []);

  const libelle = (liste, code) => (liste || []).find((x) => x.code === code)?.libelle || code || '';

  const visibles = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return (evenements || []).filter((e) => {
      if (vue === 'ouverts' && e.statut === 'clos') return false;
      if (vue === 'retard' && e.statut !== 'en_retard') return false;
      if (vue === 'muettes' && !e.sans_reponse) return false;
      if (!q) return true;
      return [e.id, e.description, e.entite_libelle, e.responsable, e.mesure]
        .some((x) => String(x || '').toLowerCase().includes(q));
    });
  }, [evenements, recherche, vue]);

  const exporter = async () => {
    try {
      const { data } = await api.get('/evenements/export', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `registre-evenements-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Export impossible', description: 'Le fichier n’a pas pu être produit.' });
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div>
          <h2 className="text-[18px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Registre des événements
          </h2>
          <p className="text-[12.5px] text-[#6B6C85] mt-0.5">
            Les réclamations, aléas et difficultés, et la mesure prise pour chacun — indicateurs 31 et 32.
          </p>
        </div>
        <div className="md:ml-auto flex gap-2">
          <button
            data-testid="evenements-export"
            onClick={exporter}
            className="h-10 px-4 rounded-xl bg-white border border-[#EFF0F6] text-[13px] font-semibold text-[#4A4B63] flex items-center gap-2 hover:border-[#C7D2FE]"
          >
            <Download className="w-4 h-4" /> Exporter le fichier
          </button>
          <button
            data-testid="evenements-nouveau"
            onClick={() => setOuvert({ ...VIDE, _nouveau: true })}
            className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 shadow-sm hover:opacity-95"
          >
            <Plus className="w-4 h-4" /> Signaler un événement
          </button>
        </div>
      </div>

      {bilan && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Tuile libelle="À analyser" valeur={bilan.a_analyser} />
          <Tuile libelle="Mesure en cours" valeur={bilan.en_cours} />
          <Tuile libelle="En retard" valeur={bilan.en_retard} alerte={bilan.en_retard > 0} />
          <Tuile libelle="Clos" valeur={bilan.clos} />
          <Tuile
            libelle="Délai moyen"
            valeur={bilan.delai_moyen === null ? '—' : `${bilan.delai_moyen} j`}
            aide={bilan.delai_moyen === null ? 'Aucun événement clôturé' : 'De la survenue à la clôture'}
          />
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex gap-1.5">
          {[['ouverts', 'À traiter'], ['retard', 'En retard'],
            ...(bilan?.sans_reponse ? [['muettes', `Sans réponse (${bilan.sans_reponse})`]] : []),
            ['tous', 'Tout le registre']].map(([k, l]) => (
            <button
              key={k}
              data-testid={`evenements-vue-${k}`}
              onClick={() => setVue(k)}
              className={`h-9 px-3.5 rounded-xl text-[12px] font-semibold transition-colors ${
                vue === k ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'bg-white border border-[#EFF0F6] text-[#6B6C85] hover:border-[#C7D2FE]'
              }`}
            >
              {l}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
          <input
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="Rechercher…"
            className="w-full h-9 pl-10 pr-4 rounded-xl bg-white border border-[#EFF0F6] text-[13px] focus:border-[#C7D2FE] outline-none"
          />
        </div>
      </div>

      <div className="soft-card overflow-hidden">
        {chargement ? (
          <div className="p-6 text-[13px] text-[#6B6C85]">Chargement…</div>
        ) : visibles.length === 0 ? (
          <div className="p-8 text-center">
            <CheckCircle2 className="w-8 h-8 text-[#C7D2FE] mx-auto mb-2" />
            <div className="text-[14px] font-semibold text-[#1A1B2E]">
              {evenements.length === 0 ? 'Le registre est vide' : 'Rien dans cette vue'}
            </div>
            <div className="text-[12.5px] text-[#6B6C85] mt-1 max-w-md mx-auto">
              {evenements.length === 0
                ? "Un registre vide n’est pas un problème — un registre qui n’existe pas en est un. Dès la première réclamation, elle se note ici."
                : 'Changez de vue pour voir le reste du registre.'}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-[#8E8FA5] border-b border-[#EFF0F6]">
                  <th className="px-4 py-2.5 font-semibold">Référence</th>
                  <th className="px-4 py-2.5 font-semibold">Date</th>
                  <th className="px-4 py-2.5 font-semibold">Type</th>
                  <th className="px-4 py-2.5 font-semibold">Origine</th>
                  <th className="px-4 py-2.5 font-semibold">Ce qui s&rsquo;est passé</th>
                  <th className="px-4 py-2.5 font-semibold">Qui s&rsquo;en charge</th>
                  <th className="px-4 py-2.5 font-semibold">Échéance</th>
                  <th className="px-4 py-2.5 font-semibold">Statut</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((e) => (
                  <tr
                    key={e.id}
                    data-testid={`evenement-${e.id}`}
                    onClick={() => setOuvert(e)}
                    className="border-b border-[#F6F7FB] last:border-0 hover:bg-[#FAFAFE] cursor-pointer"
                  >
                    <td className="px-4 py-3 font-mono text-[11.5px] text-[#6B6C85]">{e.id}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{jourFr(e.survenu_le || e.cree_le)}</td>
                    <td className="px-4 py-3">
                      {libelle(refs.types, e.type_code)}
                      {e.gravite_code === 'MAJEURE' && (
                        <span className="ml-1.5 text-[10.5px] font-semibold text-[#B91C1C]">majeure</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#4A4B63]">
                      {libelle(refs.origines, e.origine_code)}
                      {e.origine === 'satisfaction' && (
                        <span className="block text-[10.5px] text-[#8E8FA5]">posé par la plateforme</span>
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-[280px]">
                      <div className="truncate text-[#1A1B2E]">{e.description}</div>
                      {(e.reclamant || e.entite_libelle) && (
                        <div className="truncate text-[11px] text-[#8E8FA5]">
                          {[e.reclamant, e.entite_libelle].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#4A4B63]">{e.responsable || <span className="text-[#B45309]">à désigner</span>}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{jourFr(e.echeance)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-lg text-[11.5px] font-semibold ${COULEUR_STATUT[e.statut] || ''}`}>
                        {e.statut_libelle}
                      </span>
                      {e.sans_reponse && (
                        <span className="block mt-1 text-[10.5px] font-semibold text-[#B45309]">
                          sans réponse
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <FenetreEvenement
        evenement={ouvert}
        refs={refs}
        onClose={() => setOuvert(null)}
        onEnregistre={() => { setOuvert(null); charger(); }}
      />
    </div>
  );
}

function Tuile({ libelle, valeur, alerte, aide }) {
  return (
    <div className={`soft-card p-4 ${alerte ? 'border border-[#FECACA] bg-[#FEF2F2]' : ''}`}>
      <div className="text-[11px] uppercase tracking-wide text-[#8E8FA5] font-semibold">{libelle}</div>
      <div className={`text-[22px] font-bold mt-1 ${alerte ? 'text-[#B91C1C]' : 'text-[#1A1B2E]'}`}>{valeur}</div>
      {aide && <div className="text-[10.5px] text-[#8E8FA5] mt-0.5">{aide}</div>}
    </div>
  );
}

function FenetreEvenement({ evenement, refs, onClose, onEnregistre }) {
  const [v, setV] = useState(VIDE);
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState(null);
  const nouveau = !!evenement?._nouveau;

  useEffect(() => {
    if (!evenement) return;
    setV({
      ...VIDE,
      ...evenement,
      survenu_le: pourChamp(evenement.survenu_le) || new Date().toISOString().slice(0, 10),
      echeance: pourChamp(evenement.echeance),
      reponse_le: pourChamp(evenement.reponse_le),
    });
    setErreur(null);
  }, [evenement]);

  if (!evenement) return null;

  const set = (k, x) => setV((s) => ({ ...s, [k]: x }));
  const manques = evenement.manques || [];
  const attendUneReponse = TYPES_AVEC_REPONSE.includes(v.type_code);
  const manque = (champ) => manques.find((m) => m.champ === champ);
  const clos = !!evenement.clos_le;

  const corps = () => ({
    survenu_le: v.survenu_le || null,
    origine_code: v.origine_code,
    type_code: v.type_code,
    gravite_code: v.gravite_code,
    entite_type: v.entite_type || null,
    entite_id: v.entite_id || null,
    entite_libelle: v.entite_libelle || null,
    description: v.description || '',
    analyse: v.analyse || '',
    mesure: v.mesure || '',
    responsable: v.responsable || '',
    echeance: v.echeance || null,
    reclamant: v.reclamant || '',
    reponse_faite: v.reponse_faite || '',
    reponse_le: v.reponse_le || null,
    resultat: v.resultat || '',
  });

  const agir = async (quoi) => {
    setErreur(null);
    setOccupe(true);
    try {
      if (nouveau) {
        await api.post('/evenements', corps());
        toast({ title: 'Événement enregistré' });
      } else {
        await api.put(`/evenements/${evenement.id}`, corps());
        if (quoi === 'clore') {
          await api.post(`/evenements/${evenement.id}/clore`,
            { mesure: v.mesure, resultat: v.resultat });
          toast({ title: 'Événement clôturé' });
        } else if (quoi === 'rouvrir') {
          await api.post(`/evenements/${evenement.id}/rouvrir`, {});
          toast({ title: 'Événement rouvert' });
        } else {
          toast({ title: 'Événement enregistré' });
        }
      }
      onEnregistre();
    } catch (err) {
      setErreur(err?.response?.data?.detail || err?.message || 'Erreur inattendue');
    } finally {
      setOccupe(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[720px] rounded-2xl p-0 overflow-hidden">
        <div className="max-h-[88vh] overflow-y-auto">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#EFF0F6] bg-white sticky top-0 z-10">
            <DialogTitle className="text-[17px]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {nouveau ? 'Signaler un événement' : evenement.id}
            </DialogTitle>
            <DialogDescription className="text-[12.5px]">
              {evenement.origine === 'satisfaction'
                ? 'Posé par la plateforme : une satisfaction est passée sous le seuil.'
                : "Ce qui s’est passé, ce qu’on en a compris, et ce qu’on a changé."}
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 py-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Champ label="Date de l'événement">
                <input type="date" value={v.survenu_le} onChange={(e) => set('survenu_le', e.target.value)} className={cls} />
              </Champ>
              <Champ label="Gravité">
                <select value={v.gravite_code} onChange={(e) => set('gravite_code', e.target.value)} className={cls}>
                  {(refs.gravites || []).map((g) => <option key={g.code} value={g.code}>{g.libelle}</option>)}
                </select>
              </Champ>
              <Champ label="Qui l'a exprimé">
                <select value={v.origine_code} onChange={(e) => set('origine_code', e.target.value)} className={cls}>
                  {(refs.origines || []).map((o) => <option key={o.code} value={o.code}>{o.libelle}</option>)}
                </select>
              </Champ>
              <Champ label="De quoi il s'agit">
                <select value={v.type_code} onChange={(e) => set('type_code', e.target.value)} className={cls}>
                  {(refs.types || []).map((t) => <option key={t.code} value={t.code}>{t.libelle}</option>)}
                </select>
              </Champ>
              {attendUneReponse && (
                <Champ label="Qui réclame">
                  <input
                    data-testid="evenement-reclamant"
                    value={v.reclamant || ''}
                    onChange={(e) => set('reclamant', e.target.value)}
                    placeholder="Le nom de la personne"
                    className={cls}
                  />
                  <Signal m={manque('reclamant')} />
                </Champ>
              )}
              <Champ label="Ce que ça concerne" plein>
                <input
                  value={v.entite_libelle || ''}
                  onChange={(e) => set('entite_libelle', e.target.value)}
                  placeholder="La session, le programme, l'entreprise…"
                  className={cls}
                />
              </Champ>
            </div>

            <Champ label="Ce qui s'est passé" plein>
              <textarea rows={3} value={v.description} onChange={(e) => set('description', e.target.value)} className={clsZone} />
            </Champ>

            <Champ label="Analyse des causes" plein>
              <textarea
                rows={2} value={v.analyse || ''} onChange={(e) => set('analyse', e.target.value)}
                className={clsZone}
                placeholder="Pourquoi c'est arrivé — pas seulement ce qui est arrivé."
              />
              <Signal m={manque('analyse')} />
            </Champ>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Champ label="Qui s'en charge">
                <input value={v.responsable || ''} onChange={(e) => set('responsable', e.target.value)} className={cls} />
                <Signal m={manque('responsable')} />
              </Champ>
              <Champ label="Pour quand">
                <input type="date" value={v.echeance || ''} onChange={(e) => set('echeance', e.target.value)} className={cls} />
              </Champ>
            </div>

            <Champ label="Mesure prise" plein>
              <textarea
                rows={2} value={v.mesure || ''} onChange={(e) => set('mesure', e.target.value)}
                className={clsZone}
                placeholder="Ce qui a été changé pour que ça ne se reproduise pas."
              />
              <Signal m={manque('mesure')} />
            </Champ>

            {attendUneReponse && (
              <div className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-4 space-y-3">
                <div className="text-[11.5px] font-semibold text-[#6B6C85] uppercase tracking-wide">
                  La réponse faite à la personne
                </div>
                <div className="text-[11px] text-[#8E8FA5] -mt-1">
                  Ce n&rsquo;est pas la mesure : on peut changer la salle et ne jamais rappeler.
                  L&rsquo;auditeur pose les deux questions séparément.
                </div>
                <textarea
                  data-testid="evenement-reponse"
                  rows={2} value={v.reponse_faite || ''}
                  onChange={(e) => set('reponse_faite', e.target.value)}
                  className={clsZone}
                  placeholder="Ce qu'on lui a répondu, et par quel moyen."
                />
                <div className="sm:w-1/2">
                  <label className="block text-[11.5px] font-semibold text-[#6B6C85] mb-1">Répondu le</label>
                  <input
                    type="date" value={v.reponse_le || ''}
                    onChange={(e) => set('reponse_le', e.target.value)}
                    className={cls}
                  />
                </div>
                <Signal m={manque('reponse_faite')} />
                {evenement.delai_reponse !== null && evenement.delai_reponse !== undefined && (
                  <div className="text-[11px] text-[#047857]">
                    Répondu en {evenement.delai_reponse} jour{evenement.delai_reponse > 1 ? 's' : ''}.
                  </div>
                )}
              </div>
            )}

            <Champ label="Résultat obtenu" plein>
              <textarea
                rows={2} value={v.resultat || ''} onChange={(e) => set('resultat', e.target.value)}
                className={clsZone}
                placeholder="Ce que la mesure a donné."
              />
              <Signal m={manque('resultat')} />
            </Champ>

            {clos && (
              <div className="rounded-xl bg-[#F6F7FB] px-4 py-3 text-[12px] text-[#6B6C85] flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#047857]" />
                Clôturé le {jourFr(evenement.clos_le)}
                {evenement.clos_par ? ` par ${evenement.clos_par}` : ''}
                {evenement.delai !== null && evenement.delai !== undefined
                  ? ` · traité en ${evenement.delai} jour${evenement.delai > 1 ? 's' : ''}` : ''}
              </div>
            )}

            {erreur && (
              <div data-testid="evenement-erreur" className="rounded-xl bg-[#FEF2F2] border border-[#FECACA] px-4 py-3 text-[12.5px] text-[#B91C1C] flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{erreur}</span>
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-4 border-t border-[#EFF0F6] bg-[#FAFAFE] flex-row justify-end gap-2">
            <button onClick={onClose} className="h-10 px-4 rounded-xl bg-white border border-[#EFF0F6] text-[13px] font-semibold text-[#6B6C85]">
              Fermer
            </button>
            {!nouveau && clos && (
              <button
                data-testid="evenement-rouvrir" disabled={occupe}
                onClick={() => agir('rouvrir')}
                className="h-10 px-4 rounded-xl bg-white border border-[#EFF0F6] text-[13px] font-semibold text-[#4A4B63] disabled:opacity-50"
              >
                <Clock className="w-4 h-4 inline mr-1.5" /> Rouvrir
              </button>
            )}
            <button
              data-testid="evenement-enregistrer" disabled={occupe}
              onClick={() => agir('enregistrer')}
              className="h-10 px-4 rounded-xl bg-white border border-[#C7D2FE] text-[13px] font-semibold text-[#4F46E5] disabled:opacity-50"
            >
              Enregistrer
            </button>
            {!nouveau && !clos && (
              <button
                data-testid="evenement-clore" disabled={occupe}
                onClick={() => agir('clore')}
                className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold shadow-sm disabled:opacity-50"
              >
                Clôturer
              </button>
            )}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const cls = 'w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13px] focus:bg-white focus:border-[#4F46E5] outline-none transition-colors';
const clsZone = 'w-full px-3 py-2 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13px] focus:bg-white focus:border-[#4F46E5] outline-none transition-colors resize-none';

function Champ({ label, plein, children }) {
  return (
    <div className={plein ? 'sm:col-span-2' : ''}>
      <label className="block text-[11.5px] font-semibold text-[#6B6C85] mb-1">{label}</label>
      {children}
    </div>
  );
}

// Ce qui manque se dit la ou ca se corrige, et il dit pourquoi : un champ qui
// manque sans dire pourquoi est un champ qu'on laisse vide. Seule la mesure
// bloque la cloture, et c'est le serveur qui le refuse.
function Signal({ m }) {
  if (!m) return null;
  return <div className="text-[11px] text-[#B45309] mt-1">{m.motif}</div>;
}
